import { keccak256 } from "viem";

import { v2Factory, v2Pair, v2Router02 } from "../contracts/artifacts";

import { deployContract } from "./deploy-utils";

import type { DeployContext } from "./deploy-utils";
import type {
  DeploymentPhaseResult,
  FoundationAddresses,
  V2Addresses,
} from "./types";
import type { Hash } from "viem";

/**
 * Phase 2: Deploy Uniswap V2 contracts.
 *
 * Deploys UniswapV2Factory, UniswapV2Router02, and computes the
 * init code pair hash from the V2Pair creation bytecode.
 *
 * @param ctx - Deploy context (clients, deployer, RPC request)
 * @param foundation - Phase 1 addresses (need WETH9 for the router)
 */
export async function deployV2(
  ctx: DeployContext,
  foundation: FoundationAddresses,
): Promise<DeploymentPhaseResult<V2Addresses>> {
  const start = Date.now();
  let totalGas = 0n;
  const txHashes: Hash[] = [];

  // 1. Deploy UniswapV2Factory(feeToSetter = deployer)
  const factoryArtifact = v2Factory();
  const factoryResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    factoryArtifact,
    [ctx.deployer],
  );
  totalGas += factoryResult.gasUsed;
  txHashes.push(factoryResult.txHash);

  // 2. Deploy UniswapV2Router02(factory, WETH)
  const routerArtifact = v2Router02();
  const routerResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    routerArtifact,
    [factoryResult.address, foundation.weth9],
  );
  totalGas += routerResult.gasUsed;
  txHashes.push(routerResult.txHash);

  // 3. Compute init code pair hash
  // The V2 factory doesn't expose a view function for this, so we
  // compute keccak256 of the V2Pair creation bytecode directly.
  const pairArtifact = v2Pair();
  const initCodePairHash = keccak256(pairArtifact.bytecode);

  return {
    phase: 2,
    name: "Uniswap V2",
    addresses: {
      factory: factoryResult.address,
      router02: routerResult.address,
      initCodePairHash,
    },
    gasUsed: totalGas,
    durationMs: Date.now() - start,
    txHashes,
  };
}
